/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

// add 2359 - add support for EMR-hail notebook access requests
import { getSnapshot, types } from 'mobx-state-tree';

import { grantNotebookAccess, denyNotebookAccess } from '../../helpers/api';
import { displayError } from '../../helpers/notification';

const REQUESTED = 'requested';
const PROCESSING = 'processing';
const GRANTED = 'granted';
const DENIED = 'denied';

const NotebookAccessRequest = types
  .model('NotebookAccessRequest', {
    username: '',
    ns: '',
    status: types.enumeration('status', [REQUESTED, PROCESSING, GRANTED, DENIED]),
    isRead: types.boolean,
    expiry: types.string,
    requestorComments: types.maybeNull(types.string),
    adminComments: types.maybeNull(types.string),
  })
  .actions(self => ({
    updateStatus(status) {
      self.status = status;
    },
    async grant(notebookId, updateRequest) {
      self.status = PROCESSING;
      const { status, ...withoutStatus } = updateRequest;
      try {
        await grantNotebookAccess(notebookId, withoutStatus);
        self.updateStatus(GRANTED);
      } catch (e) {
        self.updateStatus(REQUESTED);
        displayError(`Error granting access for user '${self.username}'`, e);
      }
    },
    async deny(notebookId, updateRequest) {
      self.status = PROCESSING;
      try {
        await denyNotebookAccess(notebookId, self.username, self.ns, updateRequest.adminComments);
        self.updateStatus(DENIED);
      } catch (e) {
        self.updateStatus(REQUESTED);
        displayError(`Error denying access for user '${self.username}'`, e);
      }
    },
  }))
  .views(self => ({
    get id() {
      return self.identifierStr;
    },
    get identifierStr() {
      return JSON.stringify(getSnapshot(self));
    },
    requested() {
      return !self.status || self.status === REQUESTED;
    },
    processing() {
      return self.status === PROCESSING;
    },
    granted() {
      return self.status === GRANTED;
    },
    denied() {
      return self.status === DENIED;
    },
  }));

export default NotebookAccessRequest;
